import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { supabase } from "../../supabase/supabase";
import { Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

type Organization = {
  id: string;
  name: string;
  created_at: string;
  is_pro: boolean;
  member_count?: number;
};

const SuperAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState<
    Organization[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if already authenticated via localStorage
    const storedAuth = localStorage.getItem("superadmin_auth");
    if (storedAuth === "true") {
      setIsAuthenticated(true);
      fetchOrganizations();
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredOrganizations(organizations);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredOrganizations(
        organizations.filter(
          (org) =>
            org.name.toLowerCase().includes(query) ||
            org.id.toLowerCase().includes(query),
        ),
      );
    }
  }, [searchQuery, organizations]);

  const handleLogin = () => {
    if (username === "abcd" && password === "efgh") {
      setIsAuthenticated(true);
      localStorage.setItem("superadmin_auth", "true");
      fetchOrganizations();
    } else {
      toast({
        title: "Authentication Failed",
        description: "Invalid username or password",
        variant: "destructive",
      });
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("superadmin_auth");
  };

  const fetchOrganizations = async () => {
    setIsLoading(true);
    try {
      // Fetch organizations
      const { data: orgsData, error: orgsError } = await supabase
        .from("organizations")
        .select("*")
        .order("created_at", { ascending: false });

      if (orgsError) throw orgsError;

      // For each organization, get the member count
      const orgsWithMemberCount = await Promise.all(
        orgsData.map(async (org) => {
          const { count, error: membersError } = await supabase
            .from("organization_members")
            .select("*", { count: "exact", head: true })
            .eq("organization_id", org.id);

          return {
            ...org,
            member_count: membersError ? 0 : count || 0,
          };
        }),
      );

      setOrganizations(orgsWithMemberCount);
      setFilteredOrganizations(orgsWithMemberCount);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      toast({
        title: "Error",
        description: "Failed to fetch organizations",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleProStatus = async (orgId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ is_pro: !currentStatus })
        .eq("id", orgId);

      if (error) throw error;

      // Update local state
      const updatedOrgs = organizations.map((org) =>
        org.id === orgId ? { ...org, is_pro: !currentStatus } : org,
      );
      setOrganizations(updatedOrgs);
      setFilteredOrganizations(
        filteredOrganizations.map((org) =>
          org.id === orgId ? { ...org, is_pro: !currentStatus } : org,
        ),
      );

      toast({
        title: "Success",
        description: `Organization PRO status ${!currentStatus ? "enabled" : "disabled"}`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error toggling PRO status:", error);
      toast({
        title: "Error",
        description: "Failed to update PRO status",
        variant: "destructive",
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle className="text-center">Super Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium">
                  Username
                </label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
              </div>
              <Button
                className="w-full"
                onClick={handleLogin}
                disabled={!username || !password}
              >
                Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Super Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage all organizations</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleLogout}>
            Logout
          </Button>
          <Button onClick={fetchOrganizations} disabled={isLoading}>
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>PRO Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                    <p className="mt-2 text-muted-foreground">
                      Loading organizations...
                    </p>
                  </TableCell>
                </TableRow>
              ) : filteredOrganizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <p className="text-muted-foreground">
                      {searchQuery
                        ? "No organizations match your search"
                        : "No organizations found"}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrganizations.map((org) => (
                  <TableRow
                    key={org.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/organization-features/${org.id}`)}
                  >
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {org.id}
                    </TableCell>
                    <TableCell>
                      {new Date(org.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{org.member_count || 0}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          org.is_pro
                            ? "bg-good-bg text-good dark:bg-good-bg dark:text-good"
                            : "bg-muted text-foreground dark:bg-muted dark:text-foreground dark:text-ink-faint"
                        }`}
                      >
                        {org.is_pro ? "PRO" : "Standard"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click event
                          toggleProStatus(org.id, org.is_pro);
                        }}
                      >
                        {org.is_pro ? "Disable PRO" : "Enable PRO"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminPage;
